import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { IUgoPipeSpecification, IUgoVertexParamsDTO } from 'cd-eft/models/Stores/UGODesignStore';
import {
  FixtureDto,
  FixtureModalConfig,
  ICanopyInlet,
  IInputForms,
  IJunction,
  IPipeFitting,
  IRiserInlet,
  IStandardOutlet,
  IVerticalOutlet,
  IWaterSource
} from './FixtureModal.labels.config';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { UgoDesignToolsController, UgoManufacturedRiserTypeController } from 'cd-eft/services/eftSetApi';
import { getManningsN, getVerticalPipeLength } from 'cd-eft/components/Design/UGO/UGOContainer/UGOContainerVM';

const { ugoDesignStore, designStore } = stores.AppStore;

type ModalMode = 'add' | 'edit';

export class FixtureModalVM {
  @observable station: IUgoVertexParamsDTO;
  @observable mode: ModalMode;
  @observable fixtureModalConfig = FixtureModalConfig;
  @observable showCalculatedResults: boolean = false;
  @observable selectedManufacturedRiser: number = -1;
  @observable manufacturedRiserOptions: any[] = [];
  @observable.deep inputForms: IInputForms;
  @observable.deep redHighlighting = {
    FixtureMainSection: {
      fixtureType: false,
      name: false,
      station: false,
      pipeElevation: false,
      flowQ: false, // --UNUSED--
      material: false,
      pipeSize: false,
      pressurizableMain: false,
      manningN: false,
    },
    StandardOutletSection: {
      useAnimalGuard: false,
      length: false,
      head: false,
    },
    JunctionSection: {
      junctionFlowConnection: false,
      override: false,
      addedQ: false,
      addedQSource: false,
      pipelineJunction: false,
    },
    VerticalOutletSection: {
      rimElevation: false,
      pipeLength: false,
      useAnimalGuard: false,
      useRockFill: false,
      rockQuantity: false,
      useDrainPipe: false,
      useAnimalGuardDrain: false,
      material: false,
      perforated: false,
      pressurizable: false,
      pipeSize: false,
      manningsN: false
    },
    CanopyInletSection: {
      useTrashGuard: false,
      pressureFlow: false,
      terraceRequiredQ: false,
      terraceFloodElev: false,
      channelElev: false,
      terraceAndOutlet: false
    },
    RiserInletSection: {
      fixtureType: false,
      name: false,
      station: false,
      pipeElevation: false,
      useManufactureRiser: false,
      material: false,
      pipeSize: false,
      guard: false,
      topOpeningDiameter: false,
      pluggedFraction: false,
      qtop: false
    },
    PerforatedRiserSection: {
      holesPerFt: false,
      height: false,
      pluggedPerfFraction: false,
      perfShape: false,
      perfDiameter: false,
      perfWidth: false,
      perfHeight: false,
      qperf: false
    },
    WaterSourceSection: {
      terraceRequiredQ: false,
      terraceFloodElev: false,
      channelElev: false,
      terraceAndOutlet: false,
      avgFloodDepthFactor: false,
      ugoSizingFloodDepthFactor: false
    },
    OrificeOffsetSection: {
      useOrifice: false,
      orificeDepth: false,
      useDefinedSize: false,
      diameter: false,
      useOffset: false,
      offsetLength: false,
      elbowElevation: false,
      offsetMaterial: false,
      perforated: false,
      pressurizable: false,
      offsetPipeSize: false,
      offsetManningN: false
    },
    ResultsSection: {
      averageFlow: false,
      qperfPlusQtop: false,
      releaseTime: false,
      ugoSizingFlow: false,
      qperfPlusQtopUgo: false
    }
  }

  @observable.deep formsValidation = {
    FixtureMainSection: {
      fixtureType: true,
      name: true,
      station: true,
      pipeElevation: true,
      flowQ: true,
      material: true,
      pipeSize: true,
      pressurizableMain: true,
      manningN: true,
    },
    StandardOutletSection: {
      useAnimalGuard: true,
      length: true,
      head: true,
    },
    JunctionSection: {
      junctionFlowConnection: true,
      override: true,
      addedQ: true,
      addedQSource: true,
      pipelineJunction: true,
    },
    VerticalOutletSection: {
      rimElevation: true,
      pipeLength: true,
      useAnimalGuard: true,
      useRockFill: true,
      rockQuantity: true,
      useDrainPipe: true,
      useAnimalGuardDrain: true,
      material: true,
      pressurizable: true,
      pipeSize: true,
      manningsN: true
    },
    CanopyInletSection: {
      useTrashGuard: true,
      pressureFlow: true,
      terraceRequiredQ: true,
      terraceFloodElev: true,
      channelElev: true,
      terraceAndOutlet: true
    },
    RiserInletSection: {
      fixtureType: true,
      name: true,
      station: true,
      pipeElevation: true,
      useManufactureRiser: true,
      material: true,
      pipeSize: true,
      guard: true,
      topOpeningDiameter: true,
      pluggedFraction: true,
      qtop: true
    },
    PerforatedRiserSection: {
      holesPerFt: true,
      height: true,
      pluggedPerfFraction: true,
      perfShape: true,
      perfDiameter: true,
      perfWidth: true,
      perfHeight: true,
      qperf: true
    },
    WaterSourceSection: {
      terraceRequiredQ: true,
      terraceFloodElev: true,
      channelElev: true,
      terraceAndOutlet: true,
      avgFloodDepthFactor: true,
      ugoSizingFloodDepthFactor: true
    },
    OrificeOffsetSection: {
      useOrifice: true,
      orificeDepth: true,
      useDefinedSize: true,
      diameter: true,
      useOffset: true,
      offsetLength: true,
      elbowElevation: true,
      offsetMaterial: true,
      perforated: true,
      pressurizable: true,
      offsetPipeSize: true,
      offsetManningN: true
    },
    ResultsSection: {
      averageFlow: true,
      qperfPlusQtop: true,
      releaseTime: true,
      ugoSizingFlow: true,
      qperfPlusQtopUgo: true
    }
  }
  @observable applyDisabled: boolean = true;
  @observable selectedFixture: string;
  @observable accordionHeader: string;
  @observable accordionDisabled: boolean;
  @observable.deep fieldTouched = {
    FixtureMainSection: {},
    StandardOutletSection: {},
    JunctionSection: {},
    VerticalOutletSection: {},
    CanopyInletSection: {},
    RiserInletSection: {},
    PerforatedRiserSection: {},
    WaterSourceSection: {},
    OrificeOffsetSection: {},
    ResultsSection: {},
  }
  @observable canopyWaterSource: IWaterSource | null = null;
  @observable riserWaterSource: IWaterSource | null = null;
  @observable loading: boolean = false;
  @observable loadingMessage: string = '';

  constructor(station: IUgoVertexParamsDTO, mode: ModalMode) {
    this.station = JSON.parse(JSON.stringify(station));
    this.mode = mode;
    this.inputForms = this.getDefaultInputForms();
    this.selectedFixture = ugoDesignStore.fixtureTypes.get(this.station.fixtureTypeId)?.ugoFixtureTypeName ?? '';
    this.accordionHeader = this.getAccordionHeader();
    this.accordionDisabled = this.isAccordionDisabled();
    if (this.station.fixtureDtoJson) {
      this.loadFixtureDataMap[this.station.fixtureTypeId](JSON.parse(this.station.fixtureDtoJson));
    }
    this.setPressurizable(this.inputForms.FixtureMainSection.material, 'FixtureMainSection', 'pressurizableMain');
    this.setPressurizable(this.inputForms.VerticalOutletSection.material, 'VerticalOutletSection', 'pressurizable');
    this.setPressurizable(this.inputForms.OrificeOffsetSection.offsetMaterial, 'OrificeOffsetSection', 'pressurizable');
  }

  async init() {
    await this.getWaterSourceOptions();
    await this.getManufacturedRisers();
  }

  @action.bound validateField = async (fieldName: string, value: any, section: string, blockErrorCode = false): Promise<boolean> => {
    let isValid = true;

    // Junction Added Q validation - Cannot be negative
    if (fieldName === 'addedQ' && section === 'JunctionSection') {
      if (value < 0) {
        isValid = false;
        if (!blockErrorCode) {
          MessageCenter.addError('Added Q cannot be negative');
        }
      }
    }

    // Standard Outlet length validation - Use UGO_05 for out of range, warning for >20
    if (fieldName === 'length' && section === 'StandardOutletSection') {
      if (this.inputForms.StandardOutletSection.useAnimalGuard) {
        if (value === null || value === undefined || value === '') {
          isValid = false;
          if (!blockErrorCode) {
            MessageCenter.addError('Length is required');
          }
        } else {
          const numValue = parseFloat(value);
          if (numValue < 1) {
            isValid = false;
            if (!blockErrorCode) {
              MessageCenter.addErrorFromCode('UGO_05');
            }
          } else if (numValue > 20) {
            // Warning only - don't set isValid to false so Apply button stays enabled
            if (!blockErrorCode) {
              MessageCenter.addWarningFromCode('UGO_05');
            }
          }
        }
      }
    }

    // Standard Outlet head validation - Must be greater than 0
    if (fieldName === 'head' && section === 'StandardOutletSection') {
      const numValue = parseFloat(value);

      if (numValue <= 0) {
        isValid = false;
        if (!blockErrorCode) {
          if (numValue < 0) {
            MessageCenter.addError('Head cannot be negative');
          } else {
            MessageCenter.addError('Head must be greater than 0');
          }
        }
      }
    }

    // Rock quantity validation
    if (fieldName === 'rockQuantity' && section === 'VerticalOutletSection') {
      if (this.inputForms.VerticalOutletSection.useRockFill) {
        if (value === null || value === undefined || value === '') {
          isValid = false;
        } else if (value <= 0) {
          isValid = false;
          if (!blockErrorCode) {
            MessageCenter.addError('Rock quantity must be greater than 0');
          }
        }
      }
    }

    // Vertical Pipe Length validation triggered by user input in Rim Elevation - Use UGO_06 for range 0-4ft
    if (fieldName === 'rimElevation' && section === 'VerticalOutletSection') {
      fieldName = 'pipeLength'; // Change field to pipeLength for validation
      const numValue = this.inputForms.VerticalOutletSection.pipeLength; // Validate pipeLength field

      if (numValue < 0) {
        isValid = false;
        if (!blockErrorCode) {
          MessageCenter.addWarningFromCode('UGO_06');
        }
      } else if (numValue > 4) {
        // Warning only for > 4 - don't set isValid to false so Apply button stays enabled
        if (!blockErrorCode) {
          MessageCenter.addWarningFromCode('UGO_06');
        }
      }
    }

    // Material validation
    if (fieldName === 'material' || fieldName === 'offsetMaterial') {
      if (section === 'FixtureMainSection' || section === 'VerticalOutletSection' || section === 'OrificeOffsetSection') {
        if (value === null || value === undefined || value === '') {
          isValid = false;
          if (!blockErrorCode) {
            MessageCenter.addError('Material selection is required');
          }
        } else {
          let pipeSize;
          let pipeSizeName;
          if (section === 'FixtureMainSection') {
            pipeSize = this.inputForms.FixtureMainSection.pipeSize;
            pipeSizeName = 'pipeSize';
          } else if (section === 'VerticalOutletSection') {
            pipeSize = this.inputForms.VerticalOutletSection.pipeSize;
            pipeSizeName = 'pipeSize';
          } else {
            pipeSize = this.inputForms.OrificeOffsetSection.offsetPipeSize;
            pipeSizeName = 'offsetPipeSize';
          }

          if (pipeSize !== undefined && pipeSize > 0) {
            isValid = ugoDesignStore.validatePipeSize(pipeSize, Number(value), section, pipeSizeName, fieldName, this.formsValidation, this.redHighlighting, blockErrorCode);
          }
        }
      }
    }

    // Pipe size validation - Use UGO_04 for out of range
    if (fieldName === 'pipeSize' || fieldName === 'offsetPipeSize') {
      if (section === 'FixtureMainSection'
        || (section === 'VerticalOutletSection' && this.inputForms.VerticalOutletSection.useDrainPipe)
        || (section === 'OrificeOffsetSection' && this.inputForms.OrificeOffsetSection.useOffset)
      ) {
        let materialId;
        let pipeMaterialName;
        if (section === 'FixtureMainSection') {
          materialId = this.inputForms.FixtureMainSection.material;
          pipeMaterialName = 'material';
        } else if (section === 'VerticalOutletSection') {
          materialId = this.inputForms.VerticalOutletSection.material;
          pipeMaterialName = 'material';
        } else {
          materialId = this.inputForms.OrificeOffsetSection.offsetMaterial;
          pipeMaterialName = 'offsetMaterial';
        }

        if (materialId !== undefined && materialId !== '') {
          isValid = ugoDesignStore.validatePipeSize(value, materialId, section, fieldName, pipeMaterialName, this.formsValidation, this.redHighlighting, blockErrorCode);
        }
      }
    }

    // Water Source validation
    if (fieldName === 'terraceAndOutlet' && (section === 'CanopyInletSection' || section === 'WaterSourceSection')) {
      if (value === null || value === undefined || value === '' || value === '-Select-') {
        isValid = false;
        if (!blockErrorCode) {
          MessageCenter.addError('Please select a water source');
        }
      }
    }

    // Update form validation state
    this.formsValidation[section][fieldName] = isValid;

    // Always show red border if field is invalid (remove the touched logic for now)
    this.redHighlighting[section][fieldName] = !isValid;

    return isValid;
  }

  @action.bound async getWaterSourceOptions() {
    let count = 1;
    const options: object[] = [];

    try {
      const response = await UgoDesignToolsController.getUGOWaterSourceNameListUsingPOST(designStore.projectsStore.currentSessionId);
      if (response.data && response.data.waterResourceNameList) {
        response.data.waterResourceNameList.forEach(option => {
          count++;
          options.push({
            name: option,
            value: option,
            key: count
          });
        });
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.fixtureModalConfig.WaterSourceSection.terraceAndOutlet.options = options;
    }
  }

  @action.bound async getManufacturedRisers() {
    try {
      const response = await UgoManufacturedRiserTypeController.getAllManufacturedRiserTypesUsingGET();
      if (response.data) {
        this.manufacturedRiserOptions = response.data;
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound handleInputsOnChange = async (e, section) => {
    const { name, value, type, checked } = e.target;

    if (section === 'FixtureMainSection' && name === 'fixtureType') {
      this.selectedFixture = ugoDesignStore.fixtureTypes.get(Number(value))?.ugoFixtureTypeName ?? '';
      this.accordionHeader = this.getAccordionHeader();
      this.accordionDisabled = this.isAccordionDisabled();
      this.showCalculatedResults = false;
      this.resetFixtureFormData();
    }

    if (name === 'terraceAndOutlet') {
      await this.handleChangeTerraceAndOutlet(name, value, section);
      return; // Skip everything else
    }

    if ((section === 'FixtureMainSection' && name === 'material') || (section === 'VerticalOutletSection' && name === "material") || (section === 'OrificeOffsetSection' && name === "offsetMaterial")) {
      this.inputForms[section][name] = parseInt(value);
      if (section === 'FixtureMainSection') {
        this.setPressurizable(value, section, 'pressurizableMain');
      } else {
        this.setPressurizable(value, section, 'pressurizable');
      }
    } else {
      this.inputForms[section][name] = type === 'checkbox' ? checked : value;
      if (name === 'useManufactureRiser' && checked) {
        this.selectedManufacturedRiser = -1;
      }
    }

    if (section === 'VerticalOutletSection' && name === 'rimElevation') {
      // Update vertical pipe length field 
      this.inputForms.VerticalOutletSection.pipeLength = getVerticalPipeLength(value, this.station.pipeFlowElev)
    }

    // Update head calculation for Standard Outlet
    if (section === 'StandardOutletSection' && name === 'length') {
      // TODO: Replace with actual API call to calculate head based on length
      // this.inputForms.StandardOutletSection.head = await calculateHeadFromAPI(value);
    }

    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 6 && name === "perfShape") {
      this.checkManufacturedRiser();
    }

    // Validate field on change if it's been touched before
    if (this.fieldTouched[section] && this.fieldTouched[section][name]) {
      await this.validateField(name, value, section, true); // block error code to avoid spam
    }

    // Always update apply button state
    this.updateApplyDisabled();
  }

  private async handleChangeTerraceAndOutlet(name, value, section) {
    this.inputForms[section][name] = value;

    if (value && value !== '') {
      try {
        this.loading = true;
        this.loadingMessage = 'Loading water source...';
        const response = (await UgoDesignToolsController.getUGOWaterSourceFromNameUsingPOST({ sessionUuid: designStore.projectsStore.currentSessionId, waterResourceName: value })).data as IWaterSource;
        if (response) {
          if (Number(this.inputForms.FixtureMainSection.fixtureType) === 5) {
            this.canopyWaterSource = response;
          } else if (Number(this.inputForms.FixtureMainSection.fixtureType) === 6) {
            this.riserWaterSource = response;
          }

          this.inputForms[section].terraceRequiredQ = response.terraceRequiredQ;
          this.inputForms[section].terraceFloodElev = response.terraceFloodElev;
          this.inputForms[section].channelElev = response.channelElev;
          if (Number(this.inputForms.FixtureMainSection.fixtureType) === 6) {
            this.inputForms[section].avgFloodDepthFactor = response.avgFloodDepthFactor;
            this.inputForms[section].ugoSizingFloodDepthFactor = response.ugoSizingFloodDepthFactor;
          }
        }
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      } finally {
        this.loading = false;
        this.loadingMessage = '';
      }
    } else {
      this.clearWaterSource(value, section);
    }
  }

  private clearWaterSource(value, section) {
    if (this.inputForms.FixtureMainSection.fixtureType === 5) {
      this.canopyWaterSource = null;
    } else if (this.inputForms.FixtureMainSection.fixtureType === 6) {
      this.riserWaterSource = null;
    }
    this.inputForms[section].terraceAndOutlet = value;
    this.inputForms[section].terraceRequiredQ = 0;
    this.inputForms[section].terraceFloodElev = 0;
    this.inputForms[section].channelElev = 0;
    if (section === 'WaterSourceSection') {
      this.inputForms[section].avgFloodDepthFactor = 0;
      this.inputForms[section].ugoSizingFloodDepthFactor = 0;
    }
  }

  @action.bound isAccordionDisabled(): boolean {
    const singleAccordionFixtures = ['Grade Break', 'Elbow', 'Tee'];
    return !this.selectedFixture || singleAccordionFixtures.includes(this.selectedFixture);
  }

  @action.bound getAccordionHeader(): string {
    const accordionHeaderFixtures = ['Grade Break', 'Elbow', 'Tee', 'Riser Inlet'];
    const modeHeader = this.mode === 'edit' ? 'Fixture' : 'New Fixture';
    return accordionHeaderFixtures.includes(this.selectedFixture)
      ? this.selectedFixture
      : modeHeader;
  }

  @action.bound handleInputsOnBlur = (e, section) => {
    const { name, value } = e.target;
    this.validateField(name, value, section);
    this.updateApplyDisabled();
    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 6 && this.inputForms.RiserInletSection.useManufactureRiser) {
      this.checkManufacturedRiser();
    }
  }

  @action.bound toggleOverrideAddedQ() {
    this.inputForms.JunctionSection.override = !this.inputForms.JunctionSection.override;
  }

  @action.bound calculateInletQ = async () => {
    try {
      this.loading = true;

      const payload = {
        alignedElementDesignParamsId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
        ugoRiserInletJson: JSON.stringify(this.getRiserInletDto()),
      }
      const response = await UgoDesignToolsController.calculateUgoFixtureRiserInletQUsingPOST(payload);

      if (response.data && response.data.errorResponse === null) {
        const averageFlow = response.data.ugoFixtureRiserInletFlowQ?.averageFlowQ;
        const qPerf = response.data.ugoFixtureRiserInletFlowQ?.qperf;
        const qTop = response.data.ugoFixtureRiserInletFlowQ?.qtop;
        const releaseTime = response.data.ugoFixtureRiserInletFlowQ?.releaseTime;
        const ugoSizingFlow = response.data.ugoFixtureRiserInletFlowQ?.ugoSizingFlow;

        this.showCalculatedResults = true;
        this.inputForms.ResultsSection = {
          averageFlow: !averageFlow || isNaN(averageFlow) ? 0 : averageFlow,
          qperfPlusQtop: (!qPerf || isNaN(qPerf) ? 0 : qPerf) + (!qTop || isNaN(qTop) ? 0 : qTop),
          releaseTime: !releaseTime || isNaN(releaseTime) ? 'No water source' : Number(releaseTime).toFixed(2),
          ugoSizingFlow: !ugoSizingFlow || isNaN(ugoSizingFlow) ? 0 : ugoSizingFlow,
          qperfPlusQtopUgo: (!qPerf || isNaN(qPerf) ? 0 : qPerf) + (!qTop || isNaN(qTop) ? 0 : qTop),
        };
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.loading = false;
    }
  }

  @action.bound setSelectedManufacturedRiser = (index: number) => {
    this.selectedManufacturedRiser = index;
    const riser = this.manufacturedRiserOptions[index];

    // Set values
    if (riser) {
      this.inputForms.FixtureMainSection.material = ugoDesignStore.pipelineMaterialOptions.find(pm => pm.name === riser.material)?.value;
      this.inputForms.FixtureMainSection.pipeSize = riser.diameterIn;
      this.inputForms.RiserInletSection.guard = riser.guard === "CAPPED" ? riser.guard : riser.guard.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
      this.inputForms.RiserInletSection.topOpeningDiameter = riser.topOpeningDiameterIn;
      this.inputForms.PerforatedRiserSection.holesPerFt = riser.holesPerFoot;
      this.inputForms.PerforatedRiserSection.height = riser.heightOfPerforationsFt;
      this.inputForms.PerforatedRiserSection.perfShape = riser.holeDiameterIn ? "Round Perf" : "Rectangular Perf";
      if (this.inputForms.PerforatedRiserSection.perfShape === "Round Perf") {
        this.inputForms.PerforatedRiserSection.perfWidth = 0;
        this.inputForms.PerforatedRiserSection.perfHeight = 0;
        this.inputForms.PerforatedRiserSection.perfDiameter = riser.holeDiameterIn;
      } else {
        this.inputForms.PerforatedRiserSection.perfDiameter = 0;
        this.inputForms.PerforatedRiserSection.perfWidth = riser.holeWidthIn;
        this.inputForms.PerforatedRiserSection.perfHeight = riser.heightIn;
      }
      this.setPressurizable(this.inputForms.FixtureMainSection.material, 'FixtureMainSection', 'pressurizableMain');
    }
  }

  private checkManufacturedRiser() {
    const riser = this.manufacturedRiserOptions[this.selectedManufacturedRiser];
    if (riser) {
      if (
        this.inputForms.FixtureMainSection.material !== ugoDesignStore.pipelineMaterialOptions.find(pm => pm.name === riser.material)?.value ||
        this.inputForms.FixtureMainSection.pipeSize !== riser.diameterIn ||
        (this.inputForms.RiserInletSection.guard !== "CAPPED" && this.inputForms.RiserInletSection.guard !== riser.guard.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase())) ||
        (this.inputForms.RiserInletSection.guard === "CAPPED" && riser.guard !== "CAPPED") ||
        this.inputForms.RiserInletSection.topOpeningDiameter !== riser.topOpeningDiameterIn ||
        this.inputForms.PerforatedRiserSection.holesPerFt !== riser.holesPerFoot ||
        this.inputForms.PerforatedRiserSection.height !== riser.heightOfPerforationsFt ||
        (this.inputForms.PerforatedRiserSection.perfShape === 'Round Perf' && !riser.holeDiameterIn) ||
        (this.inputForms.PerforatedRiserSection.perfShape === 'Rectangular Perf' && riser.holeDiameterIn) ||
        this.inputForms.PerforatedRiserSection.perfWidth !== riser.holeWidthIn ||
        this.inputForms.PerforatedRiserSection.perfHeight !== riser.heightIn ||
        this.inputForms.PerforatedRiserSection.perfDiameter !== riser.holeDiameterIn
      ) {
        this.inputForms.RiserInletSection.useManufactureRiser = false;
      }
    }
  }

  @action.bound updateApplyDisabled = () => {
    let disabled = false;

    // Check FixtureMainSection - always required
    const mainSectionValid = Object.values(this.formsValidation.FixtureMainSection).every(valid => valid);
    if (!mainSectionValid) {
      disabled = true;
    } else if (((this.inputForms.FixtureMainSection.material != null && this.inputForms.FixtureMainSection.material > 0) || (this.inputForms.FixtureMainSection.pipeSize != null && Number(this.inputForms.FixtureMainSection.pipeSize) > 0))) {
      this.inputForms.FixtureMainSection.manningN = getManningsN(this.inputForms.FixtureMainSection.material, Number(this.inputForms.FixtureMainSection.pipeSize))
    }

    // Also check red highlighting for critical fields (for backwards compatibility with tests)
    if (this.redHighlighting.FixtureMainSection.pipeSize) {
      disabled = true;
    }

    // Check StandardOutletSection - validate both length and head if useAnimalGuard is checked
    if (this.inputForms.StandardOutletSection.useAnimalGuard) {
      // Length must be valid (>= 1) - values > 20 are warnings only, not errors
      const lengthValue = this.inputForms.StandardOutletSection.length;
      if (lengthValue < 1 || !this.formsValidation.StandardOutletSection.length) {
        disabled = true;
      }
      // Head must be valid (> 0)
      const headValue = this.inputForms.StandardOutletSection.head;
      if (headValue <= 0 || !this.formsValidation.StandardOutletSection.head) {
        disabled = true;
      }
    }

    // Check JunctionSection if it's a junction
    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 4) {
      const junctionValid = Object.values(this.formsValidation.JunctionSection).every(valid => valid);
      if (!junctionValid) {
        disabled = true;
      }
    }

    // Check VerticalOutletSection if it's a vertical outlet
    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 8) {
      // Check rim elevation - only disable for negative values & equal to 0
      const rimValue = this.inputForms.VerticalOutletSection.rimElevation;
      if (rimValue <= 0 || !this.formsValidation.VerticalOutletSection.rimElevation) {
        disabled = true;
      }

      // Check rock quantity if useRockFill is checked - must be greater than 0
      if (this.inputForms.VerticalOutletSection.useRockFill) {
        const rockValue = this.inputForms.VerticalOutletSection.rockQuantity;
        if (rockValue <= 0 || !this.formsValidation.VerticalOutletSection.rockQuantity) {
          disabled = true;
        }
      }

      // Check drain pipe fields if useDrainPipe is checked
      if (this.inputForms.VerticalOutletSection.useDrainPipe) {
        if (!this.inputForms.VerticalOutletSection.material || !this.formsValidation.VerticalOutletSection.material) {
          disabled = true;
        }
        const pipeSizeValue = this.inputForms.VerticalOutletSection.pipeSize;
        if (pipeSizeValue <= 0 || !this.formsValidation.VerticalOutletSection.pipeSize) {
          disabled = true;
        } else if (((this.inputForms.VerticalOutletSection.material != null && this.inputForms.VerticalOutletSection.material > 0) || (this.inputForms.VerticalOutletSection.pipeSize != null && Number(this.inputForms.VerticalOutletSection.pipeSize) > 0))) {
          this.inputForms.VerticalOutletSection.manningsN = getManningsN(this.inputForms.VerticalOutletSection.material, Number(this.inputForms.VerticalOutletSection.pipeSize))
        }
      }
    }

    // Check CanopyInletSection if it's a canopy inlet
    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 5) {
      if (!this.inputForms.CanopyInletSection.terraceAndOutlet || !this.formsValidation.CanopyInletSection.terraceAndOutlet) {
        disabled = true;
      }
    }

    // Check RiserInletSection
    if (Number(this.inputForms.FixtureMainSection.fixtureType) === 6) {
      if (this.inputForms.OrificeOffsetSection.useOffset) {
        if (!this.inputForms.OrificeOffsetSection.offsetMaterial || !this.formsValidation.OrificeOffsetSection.offsetMaterial) {
          disabled = true;
        }
        const pipeSizeValue = this.inputForms.OrificeOffsetSection.offsetPipeSize;
        if (pipeSizeValue <= 0 || !this.formsValidation.OrificeOffsetSection.offsetPipeSize) {
          disabled = true;
        } else if (((this.inputForms.OrificeOffsetSection.offsetMaterial != null && this.inputForms.OrificeOffsetSection.offsetMaterial > 0) || (this.inputForms.OrificeOffsetSection.offsetPipeSize != null && Number(this.inputForms.OrificeOffsetSection.offsetPipeSize) > 0))) {
          this.inputForms.OrificeOffsetSection.offsetManningN = getManningsN(this.inputForms.OrificeOffsetSection.offsetMaterial, Number(this.inputForms.OrificeOffsetSection.offsetPipeSize))
        }
      }
      if (!this.inputForms.WaterSourceSection.terraceAndOutlet || !this.formsValidation.WaterSourceSection.terraceAndOutlet) {
        disabled = true;
      }
    }

    this.applyDisabled = disabled;
  }

  private getDefaultInputForms() {
    return {
      FixtureMainSection: {
        fixtureType: this.station.fixtureTypeId,
        name: this.station.fixtureName ? this.station.fixtureName : this.station.endStation,
        station: parseFloat(this.station.endStation.replace('+', '') ?? '0'),
        pipeElevation: this.station.pipeFlowElev,
        flowQ: this.station.runoffQ,
        material: this.station.ugoPipeSpecification.materialTypeId,
        pipeSize: this.station.ugoPipeSpecification.pipelineSize,
        manningN: getManningsN(this.station.ugoPipeSpecification.materialTypeId, Number(this.station.ugoPipeSpecification.pipelineSize)),
        pressurizableMain: this.station.ugoPipeSpecification.pressurizable ?? false,
      },
      StandardOutletSection: {
        useAnimalGuard: false,
        length: 0,
        head: 0
      },
      JunctionSection: {
        junctionFlowConnection: 'Junction Flow Connection',
        override: false,
        addedQ: 0,
        addedQSource: '',
        pipelineJunction: 'No Connection',
      },
      VerticalOutletSection: {
        rimElevation: 0,
        pipeLength: 0,
        useAnimalGuard: false,
        useRockFill: false,
        rockQuantity: 0,
        useDrainPipe: true,
        useAnimalGuardDrain: false,
        material: this.station.ugoPipeSpecification.materialTypeId ?? 0,
        perforated: this.station.ugoPipeSpecification.perforated ?? false,
        pressurizable: this.station.ugoPipeSpecification.pressurizable ?? false,
        pipeSize: this.station.ugoPipeSpecification.pipelineSize ?? 0,
        manningsN: getManningsN(this.station.ugoPipeSpecification.materialTypeId, Number(this.station.ugoPipeSpecification.pipelineSize))
      },
      CanopyInletSection: {
        useTrashGuard: false,
        pressureFlow: false, // This is separate from the UGO's "pressurizable" value
        terraceRequiredQ: 0,
        terraceFloodElev: 0,
        channelElev: 0,
        terraceAndOutlet: ''
      },
      RiserInletSection: {
        fixtureType: '',
        name: '',
        station: 0,
        pipeElevation: 0,
        useManufactureRiser: false,
        material: '',
        pipeSize: 0,
        guard: '',
        topOpeningDiameter: 0,
        pluggedFraction: 0,
        qtop: 0
      },
      PerforatedRiserSection: {
        holesPerFt: 0,
        height: 0,
        pluggedPerfFraction: 0,
        perfShape: 'Round Perf',
        perfDiameter: 0,
        perfHeight: 0,
        perfWidth: 0,
        qperf: 0
      },
      WaterSourceSection: {
        terraceRequiredQ: 0,
        terraceFloodElev: 0,
        channelElev: 0,
        terraceAndOutlet: '',
        avgFloodDepthFactor: 0,
        ugoSizingFloodDepthFactor: 0
      },
      OrificeOffsetSection: {
        useOrifice: false,
        orificeDepth: 0,
        useDefinedSize: false,
        diameter: 0,
        useOffset: false,
        offsetLength: 0,
        elbowElevation: 0,
        offsetMaterial: 0,
        perforated: false,
        pressurizable: false,
        offsetPipeSize: 0,
        offsetManningN: 0
      },
      ResultsSection: {
        averageFlow: 0,
        qperfPlusQtop: 0,
        releaseTime: 'No water source',
        ugoSizingFlow: 0,
        qperfPlusQtopUgo: 0
      }
    };
  }

  @action.bound resetFixtureFormData = async () => {
    // Reset to default values but keep the FixtureMainSection as is (don't reset fixture type)
    const defaults = this.getDefaultInputForms();

    // Reset all sections except FixtureMainSection
    this.inputForms.StandardOutletSection = defaults.StandardOutletSection;
    this.inputForms.JunctionSection = defaults.JunctionSection;
    this.inputForms.VerticalOutletSection = defaults.VerticalOutletSection;
    this.inputForms.CanopyInletSection = defaults.CanopyInletSection;
    this.inputForms.RiserInletSection = defaults.RiserInletSection;
    this.inputForms.PerforatedRiserSection = defaults.PerforatedRiserSection;
    this.inputForms.WaterSourceSection = defaults.WaterSourceSection;
    this.inputForms.OrificeOffsetSection = defaults.OrificeOffsetSection;
    this.inputForms.ResultsSection = defaults.ResultsSection;

    // Reset validation states for fixture-specific sections
    this.formsValidation.StandardOutletSection = { useAnimalGuard: true, length: true, head: true };
    this.formsValidation.JunctionSection = { junctionFlowConnection: true, override: true, addedQ: true, addedQSource: true, pipelineJunction: true };
    this.formsValidation.VerticalOutletSection = { rimElevation: true, pipeLength: true, useAnimalGuard: true, useRockFill: true, rockQuantity: true, useDrainPipe: true, useAnimalGuardDrain: true, material: true, pressurizable: true, pipeSize: true, manningsN: true };
    this.formsValidation.CanopyInletSection = { useTrashGuard: true, pressureFlow: true, terraceRequiredQ: true, terraceFloodElev: true, channelElev: true, terraceAndOutlet: true };
    this.formsValidation.RiserInletSection = { fixtureType: true, name: true, station: true, pipeElevation: true, useManufactureRiser: true, material: true, pipeSize: true, guard: true, topOpeningDiameter: true, pluggedFraction: true, qtop: true };
    this.formsValidation.PerforatedRiserSection = { holesPerFt: true, height: true, pluggedPerfFraction: true, perfShape: true, perfDiameter: true, perfWidth: true, perfHeight: true, qperf: true };
    this.formsValidation.WaterSourceSection = { terraceRequiredQ: true, terraceFloodElev: true, channelElev: true, terraceAndOutlet: true, avgFloodDepthFactor: true, ugoSizingFloodDepthFactor: true };
    this.formsValidation.OrificeOffsetSection = { useOrifice: true, orificeDepth: true, useDefinedSize: true, diameter: true, useOffset: true, offsetLength: true, elbowElevation: true, offsetMaterial: true, perforated: true, pressurizable: true, offsetPipeSize: true, offsetManningN: true };
    this.formsValidation.ResultsSection = { averageFlow: true, qperfPlusQtop: true, releaseTime: true, ugoSizingFlow: true, qperfPlusQtopUgo: true };

    // Reset red highlighting for fixture-specific sections
    this.redHighlighting.StandardOutletSection = { useAnimalGuard: false, length: false, head: false };
    this.redHighlighting.JunctionSection = { junctionFlowConnection: false, override: false, addedQ: false, addedQSource: false, pipelineJunction: false };
    this.redHighlighting.VerticalOutletSection = { rimElevation: false, pipeLength: false, useAnimalGuard: false, useRockFill: false, rockQuantity: false, useDrainPipe: false, useAnimalGuardDrain: false, material: false, perforated: false, pressurizable: false, pipeSize: false, manningsN: false };
    this.redHighlighting.CanopyInletSection = { useTrashGuard: false, pressureFlow: false, terraceRequiredQ: false, terraceFloodElev: false, channelElev: false, terraceAndOutlet: false };
    this.redHighlighting.RiserInletSection = { fixtureType: false, name: false, station: false, pipeElevation: false, useManufactureRiser: false, material: false, pipeSize: false, guard: false, topOpeningDiameter: false, pluggedFraction: false, qtop: false };
    this.redHighlighting.PerforatedRiserSection = { holesPerFt: false, height: false, pluggedPerfFraction: false, perfShape: false, perfDiameter: false, perfWidth: false, perfHeight: false, qperf: false };
    this.redHighlighting.WaterSourceSection = { terraceRequiredQ: false, terraceFloodElev: false, channelElev: false, terraceAndOutlet: false, avgFloodDepthFactor: false, ugoSizingFloodDepthFactor: false };
    this.redHighlighting.OrificeOffsetSection = { useOrifice: false, orificeDepth: false, useDefinedSize: false, diameter: false, useOffset: false, offsetLength: false, elbowElevation: false, offsetMaterial: false, perforated: false, pressurizable: false, offsetPipeSize: false, offsetManningN: false };
    this.redHighlighting.ResultsSection = { averageFlow: false, qperfPlusQtop: false, releaseTime: false, ugoSizingFlow: false, qperfPlusQtopUgo: false };

    // Reset field touched states for fixture-specific sections only
    this.fieldTouched.StandardOutletSection = {};
    this.fieldTouched.JunctionSection = {};
    this.fieldTouched.VerticalOutletSection = {};
    this.fieldTouched.CanopyInletSection = {};
    this.fieldTouched.RiserInletSection = {};
    this.fieldTouched.PerforatedRiserSection = {};
    this.fieldTouched.WaterSourceSection = {};
    this.fieldTouched.OrificeOffsetSection = {};
    this.fieldTouched.ResultsSection = {};

    // Re-validate FixtureMainSection fields to ensure invalid values still show errors
    await this.validateField('material', this.inputForms.FixtureMainSection.material, 'FixtureMainSection', true);
    await this.validateField('pipeSize', this.inputForms.FixtureMainSection.pipeSize, 'FixtureMainSection', true);
    await this.validateField('name', this.inputForms.FixtureMainSection.name, 'FixtureMainSection', true);
    await this.validateField('pipeElevation', this.inputForms.FixtureMainSection.pipeElevation, 'FixtureMainSection', true);

    // Clear any error messages
    MessageCenter.clearMessages();
  }

  @action.bound setPressurizable = (materialTypeId, section, field) => {
    const selected = ugoDesignStore.pipelineMaterials.get(parseInt(materialTypeId));
    const canPressurize = selected?.canPressurizable || false;
    if (!canPressurize) {
      this.inputForms[section][field] = false;
      this.fixtureModalConfig[section][field].disabled = true;
    } else {
      this.fixtureModalConfig[section][field].disabled = false;
    }
  };

  /* START - For Updated Station on Apply */

  private getPipelineFittingDto = (): IPipeFitting => {
    const fixtureType = ugoDesignStore.fixtureTypes.get(Number(this.inputForms.FixtureMainSection.fixtureType));
    const pipelineMaterial = ugoDesignStore.pipelineMaterials.get(Number(this.inputForms.FixtureMainSection.material));
    const isPerforated = pipelineMaterial?.pipelineMaterialName?.includes('Perforated');

    const ugoPipeSpecification: IUgoPipeSpecification = {
      materialTypeId: this.inputForms.FixtureMainSection.material,
      ugoMaterialDescription: pipelineMaterial?.pipelineMaterialName ?? "",
      pipelineSize: this.inputForms.FixtureMainSection.pipeSize,
      pipeManningn: getManningsN(this.inputForms.FixtureMainSection.material, Number(this.inputForms.FixtureMainSection.pipeSize)),
      perforated: isPerforated,
      pressurizable: this.inputForms.FixtureMainSection.pressurizableMain,
      pressureFlow: this.inputForms.FixtureMainSection.fixtureType === 5 ? this.inputForms.CanopyInletSection.pressureFlow : false,
      offsetDistance: 0,
      elbowElevation: 0,
    };

    return {
      pipeFittingId: this.station.reachId,
      stationDist: this.station.stationDist,
      pipeElevation: this.station.pipeFlowElev,
      manningN: this.inputForms.FixtureMainSection.manningN,
      fixtureTypeId: Number(this.inputForms.FixtureMainSection.fixtureType),
      fixtureName: fixtureType?.ugoFixtureTypeName ?? "",
      ugoPipeSpecificationJson: JSON.stringify(ugoPipeSpecification),
    };
  }

  private getJunctionDto = (): IJunction => {
    return {
      pipeFitting: this.getPipelineFittingDto(),
      junctionFlowConnection: {
        addedQ: this.inputForms.JunctionSection.addedQ,
        addedQSource: this.inputForms.JunctionSection.addedQSource,
        overrideAddedQ: this.inputForms.JunctionSection.override,
        pipelineAndJunction: this.inputForms.JunctionSection.pipelineJunction,
      },
    };
  }

  private getCanopyInletDto = (): ICanopyInlet => {
    return {
      inletPipeFitting: this.getPipelineFittingDto(),
      useTrashGuard: this.inputForms.CanopyInletSection.useTrashGuard,
      pressureFlow: this.inputForms.CanopyInletSection.pressureFlow,
      waterSource: this.canopyWaterSource
    };
  }

  private getRiserInletDto = (): IRiserInlet => {
    const pipelineMaterial = ugoDesignStore.pipelineMaterials.get(Number(this.inputForms.OrificeOffsetSection.offsetMaterial));
    const riser = this.manufacturedRiserOptions[this.selectedManufacturedRiser];

    const fixtureDto = {
      inletPipeFittingDto: this.getPipelineFittingDto(),
      useManufactureRiser: riser ? this.inputForms.RiserInletSection.useManufactureRiser : false,
      manufactureRiserId: riser ? (this.inputForms.RiserInletSection.useManufactureRiser ? riser.riserTypeId : 0) : 0,
      inletTop: {
        guardType: this.inputForms.RiserInletSection.guard,
        topOpening: this.inputForms.RiserInletSection.topOpeningDiameter,
        pluggedFraction: this.inputForms.RiserInletSection.pluggedFraction
      },
      perforatedRiser: {
        perfShape: this.inputForms.PerforatedRiserSection.perfShape,
        holesPerFoot: this.inputForms.PerforatedRiserSection.holesPerFt,
        height: this.inputForms.PerforatedRiserSection.height,
        roundPerf: this.inputForms.PerforatedRiserSection.perfShape === 'Round Perf' ? true : false,
        perfDiameter: this.inputForms.PerforatedRiserSection.perfDiameter,
        perfWidth: this.inputForms.PerforatedRiserSection.perfWidth,
        perfHeight: this.inputForms.PerforatedRiserSection.perfHeight,
        pluggedPerfFraction: this.inputForms.PerforatedRiserSection.pluggedPerfFraction,
        qperf: this.inputForms.PerforatedRiserSection.qperf
      },
      waterSource: this.riserWaterSource,
      enableWaterSourceEdit: false,
      enableChannelEdit: false,
      orificePlate: this.inputForms.OrificeOffsetSection.useOrifice ? {
        orificeDepth: this.inputForms.OrificeOffsetSection.orificeDepth,
        diameter: this.inputForms.OrificeOffsetSection.useDefinedSize ? this.inputForms.OrificeOffsetSection.diameter : 0,
      } : null,
      useOrifice: this.inputForms.OrificeOffsetSection.useOrifice,
      userDefinedOrificeSize: this.inputForms.OrificeOffsetSection.useDefinedSize,
      useOffset: this.inputForms.OrificeOffsetSection.useOffset,
      offsetPipe: this.inputForms.OrificeOffsetSection.useOffset ? {
        materialTypeId: this.inputForms.OrificeOffsetSection.offsetMaterial,
        ugoMaterialDescription: pipelineMaterial?.pipelineMaterialName ?? "",
        pipelineSize: this.inputForms.OrificeOffsetSection.offsetPipeSize,
        pipeManningn: getManningsN(this.inputForms.OrificeOffsetSection.offsetMaterial, Number(this.inputForms.OrificeOffsetSection.offsetPipeSize)),
        perforated: this.inputForms.OrificeOffsetSection.perforated,
        pressurizable: this.inputForms.OrificeOffsetSection.pressurizable,
        offsetDistance: this.inputForms.OrificeOffsetSection.offsetLength,
        elbowElevation: this.inputForms.OrificeOffsetSection.elbowElevation
      } : null,
      riserInletDesignControl: null
    }

    if (fixtureDto.waterSource && fixtureDto.waterSource.avgFloodDepthFactor !== this.inputForms.WaterSourceSection.avgFloodDepthFactor) {
      fixtureDto.enableWaterSourceEdit = true;
      fixtureDto.waterSource.avgFloodDepthFactor = this.inputForms.WaterSourceSection.avgFloodDepthFactor;
    }

    if (fixtureDto.waterSource && fixtureDto.waterSource.ugoSizingFloodDepthFactor !== this.inputForms.WaterSourceSection.ugoSizingFloodDepthFactor) {
      fixtureDto.enableChannelEdit = true;
      fixtureDto.waterSource.ugoSizingFloodDepthFactor = this.inputForms.WaterSourceSection.ugoSizingFloodDepthFactor;
    }

    return fixtureDto;
  };

  private getStandardOutletDto = (): IStandardOutlet => {
    return {
      outletPipeFitting: this.getPipelineFittingDto(),
      useAnimalGuard: this.inputForms.StandardOutletSection.useAnimalGuard,
      length: this.inputForms.StandardOutletSection.length,
      head: this.inputForms.StandardOutletSection.head,
    };
  }

  private getVerticalOutletDto = (): IVerticalOutlet => {
    const fixtureType = ugoDesignStore.fixtureTypes.get(Number(this.inputForms.FixtureMainSection.fixtureType));
    const pipelineMaterial = ugoDesignStore.pipelineMaterials.get(Number(this.inputForms.VerticalOutletSection.material));
    const drainPipeSpecification: IUgoPipeSpecification = {
      materialTypeId: this.inputForms.VerticalOutletSection.material,
      ugoMaterialDescription: pipelineMaterial?.pipelineMaterialName ?? "",
      pipelineSize: this.inputForms.VerticalOutletSection.pipeSize,
      pipeManningn: getManningsN(this.inputForms.VerticalOutletSection.material, Number(this.inputForms.VerticalOutletSection.pipeSize)),
      perforated: this.inputForms.VerticalOutletSection.perforated,
      pressurizable: this.inputForms.VerticalOutletSection.pressurizable,
      pressureFlow: false,
      offsetDistance: 0,
      elbowElevation: 0,
    };

    return {
      outletPipeFitting: this.getPipelineFittingDto(),
      remElevation: this.inputForms.VerticalOutletSection.rimElevation,
      useAnimalGuard4Outlet: this.inputForms.VerticalOutletSection.useAnimalGuard,
      useRockFill: this.inputForms.VerticalOutletSection.useRockFill,
      rockQuantity: this.inputForms.VerticalOutletSection.rockQuantity,
      useDrainPipe: this.inputForms.VerticalOutletSection.useDrainPipe,
      useAnimalGuard4Drain: this.inputForms.VerticalOutletSection.useAnimalGuardDrain,
      drainLength: this.inputForms.VerticalOutletSection.pipeLength,
      drainPipeFitting: this.inputForms.VerticalOutletSection.useDrainPipe ? {
        pipeFittingId: this.station.endStation,
        stationDist: this.station.stationDist,
        pipeElevation: this.station.pipeFlowElev,
        manningN: this.inputForms.VerticalOutletSection.manningsN,
        fixtureTypeId: this.inputForms.FixtureMainSection.fixtureType,
        fixtureName: fixtureType?.ugoFixtureTypeName ?? "",
        ugoPipeSpecificationJson: JSON.stringify(drainPipeSpecification),
      } : null,
    };
  }

  private fixtureDtoMap: Record<number, () => FixtureDto> = {
    1: this.getPipelineFittingDto,        // Grade Break
    2: this.getPipelineFittingDto,        // Elbow
    3: this.getPipelineFittingDto,        // Tee
    4: this.getJunctionDto,               // Junction
    5: this.getCanopyInletDto,            // Canopy Inlet
    6: this.getRiserInletDto,             // Riser Inlet
    7: this.getStandardOutletDto,         // Standard Outlet
    8: this.getVerticalOutletDto,         // Vertical Outlet
  }

  getUpdatedStation(): IUgoVertexParamsDTO {
    const updatedStation = JSON.parse(JSON.stringify(this.station));
    const fixtureDto = this.fixtureDtoMap[Number(this.inputForms.FixtureMainSection.fixtureType)];

    // Update fixture values
    updatedStation.fixtureTypeId = Number(this.inputForms.FixtureMainSection.fixtureType);
    updatedStation.fixtureName = this.inputForms.FixtureMainSection.name;
    updatedStation.fixtureDtoJson = fixtureDto ? JSON.stringify(fixtureDto()) : "";

    return updatedStation;
  }

  /* END - For Updated Station on Apply */

  /* START - For Loading Fixture Data */
  private loadPipelineFittingData = (pipeFittingDto: IPipeFitting): void => {
    if (!pipeFittingDto) {
      return;
    }
    const ugoPipeSpecification: IUgoPipeSpecification = JSON.parse(pipeFittingDto.ugoPipeSpecificationJson);

    this.inputForms.FixtureMainSection.manningN = pipeFittingDto.manningN;
    this.inputForms.FixtureMainSection.material = ugoPipeSpecification.materialTypeId;
    this.inputForms.FixtureMainSection.pipeSize = ugoPipeSpecification.pipelineSize;
    this.inputForms.FixtureMainSection.pressurizableMain = ugoPipeSpecification.pressurizable ?? false;
  }

  private loadJunctionData = (junctionDto: IJunction): void => {
    if (!junctionDto) {
      return;
    }
    const pipeFitting: IPipeFitting = junctionDto.pipeFitting;

    this.loadPipelineFittingData(pipeFitting);
    this.inputForms.JunctionSection.addedQ = junctionDto.junctionFlowConnection.addedQ;
    this.inputForms.JunctionSection.addedQSource = junctionDto.junctionFlowConnection.addedQSource;
    this.inputForms.JunctionSection.override = junctionDto.junctionFlowConnection.overrideAddedQ;
    this.inputForms.JunctionSection.pipelineJunction = junctionDto.junctionFlowConnection.pipelineAndJunction;
  }

  private loadCanopyInletData = (canopyInletDto: ICanopyInlet): void => {
    if (!canopyInletDto) {
      return;
    }
    const pipeFitting: IPipeFitting = canopyInletDto.inletPipeFitting;

    this.loadPipelineFittingData(pipeFitting);
    this.inputForms.CanopyInletSection.useTrashGuard = canopyInletDto.useTrashGuard;
    this.inputForms.CanopyInletSection.pressureFlow = canopyInletDto.pressureFlow;
    this.canopyWaterSource = canopyInletDto.waterSource;
    if (canopyInletDto.waterSource) {
      this.inputForms.CanopyInletSection.terraceRequiredQ = canopyInletDto.waterSource.terraceRequiredQ;
      this.inputForms.CanopyInletSection.channelElev = canopyInletDto.waterSource.channelElev;
      this.inputForms.CanopyInletSection.terraceFloodElev = canopyInletDto.waterSource.terraceFloodElev;
      this.inputForms.CanopyInletSection.terraceAndOutlet = canopyInletDto.waterSource.terraceAndOutlet;
    }
  }

  private loadRiserInletData = (riserInletDto: IRiserInlet): void => {
    if (!riserInletDto) {
      return;
    }
    const pipeFitting: IPipeFitting = riserInletDto.inletPipeFittingDto;

    this.loadPipelineFittingData(pipeFitting);
    this.inputForms.RiserInletSection.useManufactureRiser = riserInletDto.useManufactureRiser;
    if (riserInletDto.useManufactureRiser) {
      this.selectedManufacturedRiser = Number(riserInletDto.manufactureRiserId) - 1;
    }
    this.inputForms.RiserInletSection.guard = riserInletDto.inletTop.guardType;
    this.inputForms.RiserInletSection.topOpeningDiameter = riserInletDto.inletTop.topOpening;
    this.inputForms.RiserInletSection.pluggedFraction = riserInletDto.inletTop.pluggedFraction;

    this.inputForms.PerforatedRiserSection.perfShape = riserInletDto.perforatedRiser.perfShape;
    this.inputForms.PerforatedRiserSection.holesPerFt = riserInletDto.perforatedRiser.holesPerFoot;
    this.inputForms.PerforatedRiserSection.height = riserInletDto.perforatedRiser.height;
    this.inputForms.PerforatedRiserSection.perfDiameter = riserInletDto.perforatedRiser.perfDiameter;
    this.inputForms.PerforatedRiserSection.perfWidth = riserInletDto.perforatedRiser.perfWidth;
    this.inputForms.PerforatedRiserSection.perfHeight = riserInletDto.perforatedRiser.perfHeight;
    this.inputForms.PerforatedRiserSection.pluggedPerfFraction = riserInletDto.perforatedRiser.pluggedPerfFraction;
    this.inputForms.PerforatedRiserSection.qperf = riserInletDto.perforatedRiser.qperf;

    this.riserWaterSource = riserInletDto.waterSource;

    this.inputForms.OrificeOffsetSection.useOrifice = riserInletDto.useOrifice;
    this.inputForms.OrificeOffsetSection.useDefinedSize = riserInletDto.userDefinedOrificeSize;
    if (riserInletDto.useOrifice && riserInletDto.orificePlate) {
      this.inputForms.OrificeOffsetSection.orificeDepth = riserInletDto.orificePlate.orificeDepth;
    }
    if (riserInletDto.userDefinedOrificeSize && riserInletDto.orificePlate) {
      this.inputForms.OrificeOffsetSection.diameter = riserInletDto.orificePlate.diameter;
    }
    this.inputForms.OrificeOffsetSection.useOffset = riserInletDto.useOffset;
    if (riserInletDto.useOffset && riserInletDto.offsetPipe) {
      this.inputForms.OrificeOffsetSection.offsetMaterial = riserInletDto.offsetPipe.materialTypeId;
      this.inputForms.OrificeOffsetSection.offsetPipeSize = riserInletDto.offsetPipe.pipelineSize;
      this.inputForms.OrificeOffsetSection.perforated = riserInletDto.offsetPipe.perforated ?? false;
      this.inputForms.OrificeOffsetSection.pressurizable = riserInletDto.offsetPipe.pressurizable ?? false;
      this.inputForms.OrificeOffsetSection.offsetLength = riserInletDto.offsetPipe.offsetDistance ?? 0;
      this.inputForms.OrificeOffsetSection.elbowElevation = riserInletDto.offsetPipe.elbowElevation ?? 0;
      this.inputForms.OrificeOffsetSection.offsetManningN = getManningsN(this.inputForms.OrificeOffsetSection.offsetMaterial, Number(this.inputForms.OrificeOffsetSection.offsetPipeSize));
    }

    if (riserInletDto.waterSource) {
      this.inputForms.WaterSourceSection.terraceRequiredQ = riserInletDto.waterSource.terraceRequiredQ;
      this.inputForms.WaterSourceSection.channelElev = riserInletDto.waterSource.channelElev;
      this.inputForms.WaterSourceSection.terraceFloodElev = riserInletDto.waterSource.terraceFloodElev;
      this.inputForms.WaterSourceSection.terraceAndOutlet = riserInletDto.waterSource.terraceAndOutlet;
      this.inputForms.WaterSourceSection.avgFloodDepthFactor = riserInletDto.waterSource.avgFloodDepthFactor;
      this.inputForms.WaterSourceSection.ugoSizingFloodDepthFactor = riserInletDto.waterSource.ugoSizingFloodDepthFactor;
    }
  }

  private loadStandardOutletData = (standardOutletDto: IStandardOutlet): void => {
    if (!standardOutletDto) {
      return;
    }
    const pipeFitting: IPipeFitting = standardOutletDto.outletPipeFitting;

    this.loadPipelineFittingData(pipeFitting);
    this.inputForms.StandardOutletSection.useAnimalGuard = standardOutletDto.useAnimalGuard;
    this.inputForms.StandardOutletSection.length = standardOutletDto.length;
    this.inputForms.StandardOutletSection.head = standardOutletDto.head;
  }

  private loadVerticalOutletData = (verticalOutletDto: IVerticalOutlet): void => {
    if (!verticalOutletDto) {
      return;
    }
    const pipeFitting: IPipeFitting = verticalOutletDto.outletPipeFitting;

    this.loadPipelineFittingData(pipeFitting);
    this.inputForms.VerticalOutletSection.rimElevation = verticalOutletDto.remElevation;
    this.inputForms.VerticalOutletSection.useAnimalGuard = verticalOutletDto.useAnimalGuard4Outlet;
    this.inputForms.VerticalOutletSection.useRockFill = verticalOutletDto.useRockFill;
    this.inputForms.VerticalOutletSection.rockQuantity = verticalOutletDto.rockQuantity;
    this.inputForms.VerticalOutletSection.useDrainPipe = verticalOutletDto.useDrainPipe;
    this.inputForms.VerticalOutletSection.useAnimalGuardDrain = verticalOutletDto.useAnimalGuard4Drain;
    this.inputForms.VerticalOutletSection.pipeLength = verticalOutletDto.drainLength;
    if (verticalOutletDto.useDrainPipe && verticalOutletDto.drainPipeFitting) {
      const drainPipeSpecification: IUgoPipeSpecification = JSON.parse(verticalOutletDto.drainPipeFitting.ugoPipeSpecificationJson);

      this.inputForms.VerticalOutletSection.material = drainPipeSpecification.materialTypeId;
      this.inputForms.VerticalOutletSection.pipeSize = drainPipeSpecification.pipelineSize;
      this.inputForms.VerticalOutletSection.manningsN = getManningsN(drainPipeSpecification.materialTypeId, Number(drainPipeSpecification.pipelineSize));
      this.inputForms.VerticalOutletSection.perforated = drainPipeSpecification.perforated;
      this.inputForms.VerticalOutletSection.pressurizable = drainPipeSpecification.pressurizable;
    }
  }

  private loadFixtureDataMap: Record<number, (fixtureDto) => void> = {
    1: this.loadPipelineFittingData,
    2: this.loadPipelineFittingData,
    3: this.loadPipelineFittingData,
    4: this.loadJunctionData,
    5: this.loadCanopyInletData,
    6: this.loadRiserInletData,
    7: this.loadStandardOutletData,
    8: this.loadVerticalOutletData,
  }
  /* END - For Loading Fixture Data */
}